import { CdkTextareaAutosize, TextFieldModule } from '@angular/cdk/text-field';
import { ChangeDetectionStrategy, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MediaApi, MessageApi, SpeechApi } from '@app/data/api';
import { FileType, MediaDto, MediaTarget, PromptSuggestionDto, PromptSuggestionExampleDto } from '@app/data/dto';
import { AppIcon } from '@app/shared/controls/app-icon/app-icon';
import { AudioRecorderService } from '@app/services/audio-recorder.service';
import { PromptSuggestionPreferenceService } from '@app/services/prompt-suggestion-preference.service';
import { UploadService } from '@app/services/upload.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NotificationService } from '@app/services/notification.service';
import { Subscription, firstValueFrom } from 'rxjs';
import { PromptSuggestion } from '../prompt-suggestion/prompt-suggestion';

interface UploadingMediaAttachment {
  readonly uploadId: string;
  readonly fileName: string;
  readonly progress: number;
}

export interface ChatMessageSubmitEvent {
  readonly prompt: string;
  readonly attachments: readonly MediaDto[];
}

@Component({
  selector: 'app-message-bar',
  imports: [AppIcon, FormsModule, TextFieldModule, TranslatePipe, PromptSuggestion],
  templateUrl: './message-bar.html',
  styleUrl: './message-bar.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MessageBar implements OnInit, OnDestroy {
  private static readonly MAX_ATTACHMENT_COUNT = 10;
  private static readonly MAX_ATTACHMENT_SIZE_MB = 100;

  @ViewChild('fileInput') private readonly fileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('autosize') private readonly autosize?: CdkTextareaAutosize;
  @ViewChild('visualizerHost') private readonly visualizerHost?: ElementRef<HTMLElement>;
  @ViewChild('messagebar')
  private readonly messagebar?: ElementRef<HTMLTextAreaElement>;

  private readonly notification = inject(NotificationService);
  private readonly translate = inject(TranslateService);
  private readonly recorder = inject(AudioRecorderService);
  private readonly speechApi = inject(SpeechApi);
  private readonly mediaApi = inject(MediaApi);
  private readonly messageApi = inject(MessageApi);
  private readonly uploadService = inject(UploadService);
  private readonly promptSuggestionPreference = inject(PromptSuggestionPreferenceService);
  private recorderCompleteSubscription?: Subscription;
  private lastQueuedDroppedFiles: readonly File[] | null = null;
  private visualizerAttached = false;
  private readonly promptSuggestionDebounceMs = 1000;
  private promptSuggestionDebounceTimeout?: ReturnType<typeof setTimeout>;
  private promptSuggestionAbortController?: AbortController;
  private promptSuggestionRequestId = 0;
  private retainTextareaFocus = false;

  protected readonly prompt = signal('');
  protected readonly isMenuOpen = signal(false);
  protected readonly promptSuggestionsOpen = signal(false);
  protected readonly promptSuggestionsLoading = signal(false);
  protected readonly promptSuggestionsEnabled = computed(() => this.promptSuggestionPreference.enabled());
  protected readonly promptSuggestions = signal<readonly PromptSuggestionDto[]>([]);
  protected readonly isRecording = signal(false);
  protected readonly isTranscribing = signal(false);
  protected readonly mediaAttachments = signal<MediaDto[]>([]);
  protected readonly uploadingMediaAttachments = signal<UploadingMediaAttachment[]>([]);
  protected readonly hasActiveUploads = computed(() => this.uploadingMediaAttachments().length > 0);
  protected readonly hasAttachments = computed(() => this.mediaAttachments().length > 0 || this.hasActiveUploads());
  protected readonly hasPromptSuggestions = computed(() => this.promptSuggestions().length > 0);
  protected readonly showPromptSuggestionsButton = computed(() => this.promptSuggestionsLoading() || this.hasPromptSuggestions());
  readonly initialAttachments = input<readonly MediaDto[]>([]);
  readonly initialPrompt = input<string | null>(null);
  readonly initialPromptRevision = input(0);
  readonly focusRevision = input(0);
  readonly droppedFiles = input<readonly File[]>([]);
  readonly activeConversationId = input<string | null>(null);
  readonly isWaitingForResponse = input(false);
  readonly isStreaming = input(false);
  readonly messageSubmitted = output<ChatMessageSubmitEvent>();
  readonly promptChanged = output<string>();
  readonly streamStopped = output<void>();

  constructor() {
    effect(() => {
      const files = this.droppedFiles();
      if (files.length === 0 || files === this.lastQueuedDroppedFiles) {
        return;
      }

      this.lastQueuedDroppedFiles = files;
      void this.queueFiles(files);
    });

    effect(() => {
      const attachments = this.initialAttachments();
      if (attachments.length === 0) {
        return;
      }

      this.mediaAttachments.set([...attachments]);
    });

    effect(() => {
      this.initialPromptRevision();
      const prompt = this.initialPrompt();
      if (prompt == null) {
        return;
      }

      untracked(() => this.applyPrompt(prompt));
    });

    effect(() => {
      this.focusRevision();
      this.retainTextareaFocus = true;
      this.focusTextareaWhenAvailable();
    });

    effect(() => {
      const busy = this.isBusy;
      if (busy && this.isTextareaFocused()) {
        this.retainTextareaFocus = true;
        return;
      }

      if (!busy && this.retainTextareaFocus) {
        this.focusTextareaWhenAvailable();
      }
    });
  }

  protected get canSend(): boolean {
    return this.prompt().trim().length > 0 && !this.isBusy && !this.hasActiveUploads();
  }

  protected get isBusy(): boolean {
    return this.isTranscribing() || this.isWaitingForResponse() || this.isStreaming();
  }

  protected get showSpinner(): boolean {
    return this.isTranscribing() || this.isWaitingForResponse() || this.hasActiveUploads();
  }

  protected toggleMenu(): void {
    this.isMenuOpen.update((isOpen) => !isOpen);
  }

  protected uploadFile(): void {
    this.isMenuOpen.set(false);
    this.fileInput?.nativeElement.click();
  }

  protected showUnavailable(featureKey: string): void {
    this.isMenuOpen.set(false);
    this.notification.info(this.translate.instant('chat.messageBar.unavailable', {
      feature: this.translate.instant(featureKey),
    }));
  }

  protected onPromptChange(value: string): void {
    this.setPrompt(value);
    this.evaluatePromptForSuggestions(value);
  }

  protected onTextareaFocus(): void {
    this.retainTextareaFocus = true;
  }

  protected preserveTextareaFocus(event: MouseEvent): void {
    if (!this.isTextareaFocused()) {
      return;
    }

    this.retainTextareaFocus = true;
    event.preventDefault();
  }

  protected togglePromptSuggestions(): void {
    if (!this.hasPromptSuggestions()) {
      return;
    }

    this.promptSuggestionsOpen.update((isOpen) => !isOpen);
  }

  protected closePromptSuggestions(): void {
    this.promptSuggestionsOpen.set(false);
  }

  protected async refreshPromptSuggestions(): Promise<void> {
    await this.loadPromptSuggestions(this.prompt(), true);
  }

  protected insertPromptSuggestionExample(example: PromptSuggestionExampleDto): void {
    const currentPrompt = this.prompt().trimEnd();
    const separator = currentPrompt ? '\n\n' : '';
    this.setPrompt(`${currentPrompt}${separator}${example.example}`);
    this.promptSuggestionsOpen.set(false);
    this.focusTextarea();
    queueMicrotask(() => this.autosize?.resizeToFitContent(true));
  }

  protected onEnter(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.shiftKey) {
      return;
    }

    event.preventDefault();
    if (this.canSend) {
      this.sendMessage();
    }
  }

  protected focusTextarea(): void {
    this.messagebar?.nativeElement.focus({ preventScroll: true });
  }

  protected sendMessage(): void {
    if (this.isBusy || this.hasActiveUploads()) {
      return;
    }

    const nextPrompt = this.prompt().trim();
    if (!nextPrompt) {
      return;
    }

    this.focusTextarea();
    this.retainTextareaFocus = true;

    this.messageSubmitted.emit({
      prompt: nextPrompt,
      attachments: this.mediaAttachments(),
    });
    this.setPrompt('');
    this.promptSuggestions.set([]);
    this.promptSuggestionsOpen.set(false);
    this.clearPromptSuggestionDebounce();
    this.abortPromptSuggestionRequest();
    this.clearAttachments();

    queueMicrotask(() => this.autosize?.resizeToFitContent(true));
  }

  protected async toggleRecording(): Promise<void> {
    if (this.isStreaming()) {
      this.streamStopped.emit();
      return;
    }

    if (this.isWaitingForResponse() || this.isTranscribing() || this.hasActiveUploads()) {
      return;
    }

    if (this.canSend) {
      this.sendMessage();
      return;
    }

    if (this.isRecording()) {
      this.stopRecording();
      return;
    }

    await this.startRecording();
  }

  protected onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    if (files.length === 0) {
      return;
    }

    void this.queueFiles(files);
  }

  protected onTextareaPaste(event: ClipboardEvent): void {
    const files = this.getClipboardFiles(event.clipboardData);
    if (files.length === 0) {
      return;
    }

    event.preventDefault();
    void this.queueFiles(files);
  }

  private getClipboardFiles(dataTransfer: DataTransfer | null): readonly File[] {
    const files = Array.from(dataTransfer?.files ?? []);
    if (files.length > 0) {
      return files;
    }

    return Array.from(dataTransfer?.items ?? [])
      .filter((item) => item.kind === 'file')
      .map((item) => item.getAsFile())
      .filter((file): file is File => file != null);
  }

  private async queueFiles(files: readonly File[]): Promise<void> {
    const acceptedFiles = await this.validateSelectedFiles(files);
    this.queueAcceptedFiles(acceptedFiles);
  }

  private queueAcceptedFiles(acceptedFiles: readonly File[]): void {
    const queueGroupId = crypto.randomUUID();
    for (const file of acceptedFiles) {
      const uploadId = crypto.randomUUID();
      this.uploadingMediaAttachments.update((attachments) => [...attachments, { uploadId, fileName: file.name, progress: 0 }]);
      void this.uploadMediaAttachment(uploadId, file, queueGroupId);
    }
  }

  protected removeMediaAttachment(mediaId: string): void {
    this.mediaAttachments.update((attachments) => attachments.filter((media) => media.mediaId !== mediaId));
  }

  protected getUploadingMediaKind(attachment: UploadingMediaAttachment): string {
    const extension = attachment.fileName.split('.').pop()?.toUpperCase();
    if (extension) {
      return this.translate.instant('chat.messageBar.uploadingFileKind', { extension, progress: attachment.progress });
    }

    return this.translate.instant('chat.messageBar.uploadingFile', { progress: attachment.progress });
  }

  protected getMediaKind(media: MediaDto): string {
    const extension = media.fileName.split('.').pop()?.toUpperCase();
    if (extension) {
      return this.translate.instant('chat.messageBar.fileKind.extension', { extension });
    }

    return this.translate.instant('chat.messageBar.fileKind.file');
  }

  protected isImageMedia(media: MediaDto): boolean {
    return media.fileType === FileType.Image && !!media.mediaUrl;
  }

  ngOnInit(): void {
    this.promptSuggestionPreference.load();
  }

  ngOnDestroy(): void {
    this.recorderCompleteSubscription?.unsubscribe();
    this.clearPromptSuggestionDebounce();
    this.abortPromptSuggestionRequest();
    if (this.isRecording()) {
      this.recorder.stop();
    }
    this.detachVisualizer();
    this.clearAttachments();
  }

  @HostListener('document:click', ['$event'])
  protected closeMenuOnOutsideClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target?.closest('app-message-bar')) {
      this.isMenuOpen.set(false);
      this.promptSuggestionsOpen.set(false);
    }
  }

  private evaluatePromptForSuggestions(value: string): void {
    if (!this.promptSuggestionsEnabled() || this.isBusy) {
      return;
    }

    const wordCount = this.countWords(value);
    if (wordCount < 8) {
      this.clearPromptSuggestionDebounce();
      this.abortPromptSuggestionRequest();
      this.promptSuggestions.set([]);
      this.promptSuggestionsOpen.set(false);
      return;
    }

    this.clearPromptSuggestionDebounce();
    this.abortPromptSuggestionRequest();
    this.promptSuggestionDebounceTimeout = setTimeout(() => {
      void this.loadPromptSuggestions(this.prompt(), false);
    }, this.promptSuggestionDebounceMs);
  }

  private applyPrompt(value: string): void {
    this.setPrompt(value, false);
    this.promptSuggestions.set([]);
    this.promptSuggestionsOpen.set(false);
    this.clearPromptSuggestionDebounce();
    this.abortPromptSuggestionRequest();
    this.evaluatePromptForSuggestions(value);

    queueMicrotask(() => {
      this.focusTextarea();
      this.autosize?.resizeToFitContent(true);
    });
  }

  private focusTextareaWhenAvailable(): void {
    queueMicrotask(() => {
      if (!this.messagebar || this.isRecording() || this.isTranscribing()) {
        return;
      }

      this.focusTextarea();
    });
  }

  private isTextareaFocused(): boolean {
    return document.activeElement === this.messagebar?.nativeElement;
  }

  private setPrompt(value: string, emitChange = true): void {
    this.prompt.set(value);
    if (emitChange) {
      this.promptChanged.emit(value);
    }
  }

  private async loadPromptSuggestions(value: string, force: boolean): Promise<void> {
    const prompt = value.trim();
    if (!this.promptSuggestionsEnabled() || prompt.length === 0 || this.countWords(prompt) < 8 || this.isBusy) {
      return;
    }

    this.abortPromptSuggestionRequest();
    const requestId = ++this.promptSuggestionRequestId;
    const abortController = new AbortController();
    this.promptSuggestionAbortController = abortController;
    this.promptSuggestionsLoading.set(true);
    if (force) {
      this.promptSuggestionsOpen.set(true);
    }

    try {
      const suggestions = await firstValueFrom(this.messageApi.getPromptSuggestions({
        prompt,
        conversationId: this.activeConversationId(),
      }, abortController));

      if (requestId !== this.promptSuggestionRequestId) {
        return;
      }

      this.promptSuggestions.set(suggestions);
      if (force) {
        this.promptSuggestionsOpen.set(suggestions.length > 0);
      }
    } catch {
      if (force && !abortController.signal.aborted) {
        this.notification.error(this.translate.instant('chat.promptSuggestions.error'));
      }
    } finally {
      if (requestId === this.promptSuggestionRequestId) {
        this.promptSuggestionsLoading.set(false);
        this.promptSuggestionAbortController = undefined;
      }
    }
  }

  private clearPromptSuggestionDebounce(): void {
    if (this.promptSuggestionDebounceTimeout) {
      clearTimeout(this.promptSuggestionDebounceTimeout);
      this.promptSuggestionDebounceTimeout = undefined;
    }
  }

  private abortPromptSuggestionRequest(): void {
    this.promptSuggestionRequestId++;
    this.promptSuggestionAbortController?.abort();
    this.promptSuggestionAbortController = undefined;
    this.promptSuggestionsLoading.set(false);
  }

  private countWords(value: string): number {
    return value.trim().split(/\s+/).filter(Boolean).length;
  }

  private async startRecording(): Promise<void> {
    if (this.isBusy) {
      return;
    }

    this.isRecording.set(true);
    this.recorderCompleteSubscription?.unsubscribe();
    this.recorderCompleteSubscription = this.recorder.completeWithContext$.subscribe(({ blob, context }) => {
      if (context === 'messagebar') {
        this.transcribe(blob);
      }
    });

    try {
      await this.recorder.start({ context: 'messagebar' });
      globalThis.setTimeout(() => this.attachVisualizer(), 0);
    } catch {
      this.isRecording.set(false);
      this.notification.error(this.translate.instant('chat.messageBar.errors.microphoneStart'));
    }
  }

  private stopRecording(): void {
    this.isRecording.set(false);
    this.detachVisualizer();
    this.recorder.stop();
  }

  private transcribe(blob: Blob): void {
    this.isTranscribing.set(true);
    this.speechApi.speechToText(blob).subscribe({
      next: (response) => {
        const transcript = response.message?.trim();
        if (transcript) {
          this.setPrompt(`${this.prompt()}${this.prompt() ? ' ' : ''}${transcript}`);
          queueMicrotask(() => this.autosize?.resizeToFitContent(true));
        }
        this.isTranscribing.set(false);
      },
      error: () => {
        this.isTranscribing.set(false);
        this.notification.error(this.translate.instant('chat.messageBar.errors.transcription'));
      },
    });
  }

  private attachVisualizer(): void {
    if (this.visualizerAttached || !this.visualizerHost) {
      return;
    }

    this.recorder.attachVisualizer(this.visualizerHost.nativeElement, { bars: 129, mode: 'history' });
    this.visualizerAttached = true;
  }

  private detachVisualizer(): void {
    if (!this.visualizerAttached) {
      return;
    }

    this.recorder.detachVisualizer();
    this.visualizerAttached = false;
  }

  private clearAttachments(): void {
    this.mediaAttachments.set([]);
    this.uploadingMediaAttachments.set([]);
  }

  private async validateSelectedFiles(files: readonly File[]): Promise<readonly File[]> {
    const acceptedFiles: File[] = [];
    const maxBytes = MessageBar.MAX_ATTACHMENT_SIZE_MB * 1024 * 1024;
    const existingCount = this.mediaAttachments().length + this.uploadingMediaAttachments().length;
    let remainingSlots = Math.max(0, MessageBar.MAX_ATTACHMENT_COUNT - existingCount);

    for (const file of files) {
      if (remainingSlots <= 0) {
        this.notification.error(this.translate.instant('chat.messageBar.errors.maxFiles', { count: MessageBar.MAX_ATTACHMENT_COUNT }));
        continue;
      }

      if (file.size > maxBytes) {
        this.notification.error(this.translate.instant('chat.messageBar.errors.maxFileSize', { name: file.name, size: MessageBar.MAX_ATTACHMENT_SIZE_MB }));
        continue;
      }

      acceptedFiles.push(file);
      remainingSlots -= 1;
    }

    return acceptedFiles;
  }

  private async uploadMediaAttachment(uploadId: string, file: File, queueGroupId: string): Promise<void> {
    try {
      const uploaded = await this.uploadService.upload(file, {
        queueGroupId,
        maxConcurrentUploads: 3,
        showInQueue: false,
        mediaTarget: MediaTarget.Chat,
        progressChanged: (item) => this.updateUploadingMediaProgress(uploadId, item.progress),
      });
      const uploadedMedia = await this.getUploadedMedia(uploaded.mediaId, uploaded.fileName);
      this.removeUploadingMediaAttachment(uploadId);
      this.mediaAttachments.update((attachments) => [...attachments, uploadedMedia]);
    } catch (error) {
      this.removeUploadingMediaAttachment(uploadId);
      this.notification.error(error instanceof Error && error.message ? error.message : this.translate.instant('chat.messageBar.errors.uploadFailed', { name: file.name }));
    }
  }

  private async getUploadedMedia(mediaId: string, fileName: string): Promise<MediaDto> {
    try {
      return await firstValueFrom(this.mediaApi.get(mediaId));
    } catch {
      return { mediaId, fileName };
    }
  }

  private updateUploadingMediaProgress(uploadId: string, progress: number): void {
    const uploadProgress = this.clampProgress(progress);
    this.uploadingMediaAttachments.update((attachments) => attachments.map((attachment) => attachment.uploadId === uploadId
      ? { ...attachment, progress: uploadProgress }
      : attachment));
  }

  private removeUploadingMediaAttachment(uploadId: string): void {
    this.uploadingMediaAttachments.update((attachments) => attachments.filter((attachment) => attachment.uploadId !== uploadId));
  }

  private clampProgress(progress: number): number {
    if (!Number.isFinite(progress)) {
      return 0;
    }

    return Math.min(100, Math.max(0, Math.round(progress)));
  }
}
